import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight } from 'assets/icons';
import Button from 'components/button';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import Pagination from 'components/pagination/pagination';
import UseContext from 'hooks/useContext';
import InvoiceSkeletonRows from './InvoiceSkeletonRows';
import InvoiceRow, {
  CHECKBOX_CLASS_NAME,
  COMPANY_LINK_CLASS_NAME,
  formatEuros,
} from './InvoiceRow';
import {
  groupNameText,
  groupSubtitleText,
  needsAttention,
  statusSummaryText,
} from './invoiceGroupText';
import isRowClickIgnored from './isRowClickIgnored';
import useGroupInvoices from './useGroupInvoices';
import useOpenCompany from './useOpenCompany';
import { PAGE_SIZE } from './useMonthInvoices';

// The box, the five columns and the eye.
const COLUMN_COUNT = 7;
const SKELETON_MAX_ROWS = 5;

// Borders go on the cells: dashed ones on `tr` do not render in a
// collapsed-border table.
const SOLID_BORDER = 'border-b border-gray-100';
const DASHED_BORDER = 'border-b border-dashed border-gray-200';

const NOTHING = [];

// Ticked when every invoice of the row is, half when some are. At every
// render: a click clears `indeterminate`, and a row half ticked stays so
// while its invoices are read.
const GroupCheckbox = ({ state, onToggle, disabled, label }) => {
  const box = useRef(null);
  useEffect(() => {
    box.current.indeterminate = state === 'some';
  });
  return (
    <input
      ref={box}
      type="checkbox"
      className={`${CHECKBOX_CLASS_NAME} disabled:cursor-wait`}
      checked={state === 'all'}
      onChange={onToggle}
      disabled={disabled}
      aria-busy={disabled || undefined}
      aria-label={label}
    />
  );
};

/**
 * "none", "some" or "all" of `numbers` ticked. Numbers that are not as many
 * as the invoices of the row (`count`) are only part of them: never "all".
 */
const tickStateOf = (numbers, isSelected, count) => {
  const ticked = numbers.filter(isSelected).length;
  if (ticked === 0) return 'none';
  return ticked === numbers.length && numbers.length === count ? 'all' : 'some';
};

/**
 * The remaining amount of a row. Only part of its invoices may have a known
 * one (`remainingComplete` false): their sum is shown, and says so.
 */
const GroupRemaining = ({ group, text }) => {
  if (group.remainingAmount == null) {
    return <span className="text-gray-400">-</span>;
  }
  if (group.remainingComplete === false) {
    return (
      <span
        className="underline decoration-dotted cursor-help"
        title={text(
          'remainingPartial',
          'Reste connu pour une partie des factures seulement',
        )}
      >
        {formatEuros(group.remainingAmount)}
      </span>
    );
  }
  return formatEuros(group.remainingAmount);
};

/**
 * One row of the grouped invoice list (JBWEB-1309), a company or a group, with
 * its totals and the statuses of its invoices; its chevron unfolds it, and its
 * invoices come under it, read then. A click elsewhere on the row unfolds it
 * like the chevron; the name of a company opens its slide-over. Its box ticks every invoice of the row, folded or not:
 * they are read from the api, then added to the selection.
 *
 * `listQuery` (`useMonthInvoices`) is what the grouped list was read with;
 * `refreshedWith` changes when it is read again; `groupBy` is "company" or
 * "group"; `selection` comes from `useInvoiceSelection`.
 */
const InvoiceGroupRow = ({
  group,
  groupBy,
  listQuery,
  refreshedWith,
  selection,
}) => {
  const { language } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`invoiceList.grouping.${key}`, fallback);
  const listText = (key, fallback) =>
    language.getValue(`invoiceList.${key}`, fallback);
  const openCompany = useOpenCompany();
  const isCompany = group.type === 'company';

  const {
    isOpen,
    toggleOpen,
    list,
    isLoading,
    failed,
    page,
    setPage,
    reload,
    readAll,
    isSelecting,
  } = useGroupInvoices(group, listQuery, refreshedWith);

  // The invoices of the row: those of an unfolded row on one page, which are
  // all of them and the latest read; otherwise those read when it was last
  // ticked. The row may have had more since (a discovery, an import): the
  // count of the row, read with the list, tells.
  const [readNumbers, setReadNumbers] = useState(null);
  const shownItems = list?.items ?? NOTHING;
  const knownNumbers =
    list?.pages === 1
      ? shownItems.map((invoice) => invoice.number)
      : readNumbers;
  let tickState;
  if (knownNumbers) {
    tickState = tickStateOf(
      knownNumbers,
      selection.isSelected,
      group.invoiceCount,
    );
  } else {
    tickState = shownItems.some((invoice) =>
      selection.isSelected(invoice.number),
    )
      ? 'some'
      : 'none';
  }

  // The invoices shown give their status to the ticked ones, as the page
  // does in the flat list: after `forgetErrors`, one still in error is again.
  const { seeInvoices, statuses } = selection;
  useEffect(() => {
    if (shownItems.length > 0) seeInvoices(shownItems);
  }, [shownItems, seeInvoices, statuses]);

  const name = groupNameText(text, group);

  const toggleGroup = () => {
    if (tickState === 'all') {
      selection.unselectMany(knownNumbers);
      return;
    }
    readAll(
      (invoices) => {
        setReadNumbers(invoices.map((invoice) => invoice.number));
        selection.selectMany(invoices);
      },
      () =>
        toast.error(
          fillText(
            text(
              'selectFailed',
              'Les factures de {name} n’ont pas pu être sélectionnées, réessayez.',
            ),
            { name },
          ),
        ),
    );
  };

  const isAttention = needsAttention(group.statusCounts);

  let unfolded = null;
  if (isOpen && isLoading) {
    unfolded = (
      <InvoiceSkeletonRows
        count={Math.max(
          1,
          Math.min(SKELETON_MAX_ROWS, group.invoiceCount ?? SKELETON_MAX_ROWS),
        )}
        label={listText('loading', 'Chargement des factures…')}
      />
    );
  } else if (isOpen && failed) {
    unfolded = (
      <tr>
        <td colSpan={COLUMN_COUNT} className={`px-4 py-3 ${SOLID_BORDER}`}>
          <div
            role="alert"
            className="flex items-center gap-3 text-sm text-gray-700"
          >
            {listText(
              'loadFailed',
              'La liste des factures n’a pas pu être chargée.',
            )}
            <Button color="SECONDARY" onClick={reload}>
              {listText('retry', 'Réessayer')}
            </Button>
          </div>
        </td>
      </tr>
    );
  } else if (isOpen && list) {
    const hasPagination = list.pages > 1;
    unfolded = (
      <>
        {shownItems.map((invoice, index) => (
          <InvoiceRow
            key={invoice.number}
            invoice={invoice}
            isSelected={selection.isSelected(invoice.number)}
            onToggle={selection.toggle}
            isSelectionLocked={isSelecting}
            borderClassName={
              index === shownItems.length - 1 && !hasPagination
                ? SOLID_BORDER
                : DASHED_BORDER
            }
          />
        ))}
        {hasPagination && (
          <tr>
            <td colSpan={COLUMN_COUNT} className={`px-4 py-2 ${SOLID_BORDER}`}>
              <Pagination
                currentPage={page}
                countPerPage={PAGE_SIZE}
                countTotal={list.total}
                totalPages={list.pages}
                setCurrentPage={setPage}
                customText={fillText(
                  listText(
                    'showingRange',
                    'Affichage de {start}-{end} sur {total} résultats',
                  ),
                  {
                    start: PAGE_SIZE * (page - 1) + 1,
                    end: Math.min(PAGE_SIZE * page, list.total),
                    total: list.total,
                  },
                )}
              />
            </td>
          </tr>
        )}
      </>
    );
  }

  // Dashed only when invoices (or their placeholder) follow; an empty page
  // leaves the row to close the block.
  const hasBlock = isOpen && (isLoading || failed || shownItems.length > 0);
  const rowBorder = hasBlock ? DASHED_BORDER : SOLID_BORDER;
  const cell = (base) => `${base} ${rowBorder}`;

  return (
    <>
      <tr
        className="bg-white hover:bg-gray-50 cursor-pointer"
        onClick={(event) => {
          if (!isRowClickIgnored(event)) toggleOpen();
        }}
      >
        {/* Ticking the row never folds nor unfolds it. */}
        <td className={cell('py-3 pl-4 pr-2 w-px')} data-row-click-ignore>
          <GroupCheckbox
            state={tickState}
            onToggle={toggleGroup}
            disabled={isSelecting}
            label={fillText(
              text('selectGroup', 'Sélectionner les factures de {name}'),
              { name },
            )}
          />
        </td>
        <td colSpan={2} className={cell('px-2 py-3')}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={fillText(
                isOpen
                  ? text('fold', 'Replier {name}')
                  : text('unfold', 'Déplier {name}'),
                { name },
              )}
              onClick={(event) => {
                event.stopPropagation();
                toggleOpen();
              }}
              className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200"
            >
              {isOpen ? (
                <ChevronDown wh={16} color="#374151" />
              ) : (
                <ChevronRight wh={16} color="#374151" />
              )}
            </button>
            <div className="flex flex-col items-start">
              {isCompany ? (
                <button
                  type="button"
                  className={`${COMPANY_LINK_CLASS_NAME} font-medium text-gray-900`}
                  onClick={(event) => {
                    event.stopPropagation();
                    openCompany(group.id);
                  }}
                >
                  {name}
                </button>
              ) : (
                <span className="font-medium text-gray-900">{name}</span>
              )}
              <span className="text-xs text-gray-500">
                {groupSubtitleText(text, group, groupBy)}
              </span>
            </div>
          </div>
        </td>
        <td
          className={cell(
            'px-2 py-3 whitespace-nowrap font-medium text-gray-900',
          )}
        >
          {formatEuros(group.totalAmountInclTax ?? 0)}
        </td>
        <td className={cell('px-2 py-3 whitespace-nowrap')}>
          <GroupRemaining group={group} text={text} />
        </td>
        <td
          className={cell(
            `px-2 py-3 text-sm ${
              isAttention ? 'text-red-600 font-medium' : 'text-gray-500'
            }`,
          )}
        >
          {statusSummaryText(text, group.statusCounts)}
        </td>
        <td className={cell('py-3 pl-2 pr-3')} />
      </tr>
      {unfolded}
    </>
  );
};

export default InvoiceGroupRow;
